// audio-recording widget — capture and play back audio from the system microphone.
//
// architecture:
//   container (root, eventMode=static)
//     bgGfx    — background fill + border
//     waveGfx  — waveform visualization (live during recording, static after)
//     micGfx   — microphone icon (idle state only)
//     btnGfx   — primary action button (record / stop / play / pause)
//     statusText — timer / state label
//     infoText   — duration + file size after recording
//     errorText  — permission-denied message
//
// state machine:
//   idle → requesting → recording → processing → ready ↔ playing
//   ready → fetching → ready (remote peer: snatching the blob before playback)
//   any error → error → (click) → requesting → ...
//
// storage:
//   storeBlobFromFile() via the blob worker writes OPFS bytes + IndexedDB
//   metadata (browser mode). in tauri mode, the blob worker's blake3 hasher
//   has no midden module bundled to hash with and always returns "" — so
//   tauri recordings are routed through the rust `blob_insert` dispatch
//   instead (real blake3 computed in rust, and the bytes get pre-warmed
//   into the FsStore so iroh-blobs can actually serve them to peers). no
//   OPFS/IndexedDB mirror here — tauri never reads blobs back through the
//   browser blob-store (media playback, locality checks, and preview data
//   all go through rust dispatch calls like `blob_get_path`/`blob_get`),
//   and a mirror write here was keyed under a bogus hash (the browser
//   blake3 hasher degrades to an empty string in a tauri build), which
//   OPFS rejects outright — that threw past the doc-metadata update below,
//   so the recording succeeded in rust but the widget's document never
//   recorded blobId/blake3/etc, exactly the file-utils.ts uploadFile() bug
//   this mirrors the fix for.
//   immediate post-record playback uses URL.createObjectURL() on the in-memory blob.
//   restore-from-doc playback uses getAudioBlobData() (below), which reads
//   OPFS in browser mode and falls back to the rust-side `blob_get` dispatch
//   in tauri mode — the plain getBlobData() re-exported from blob-store.ts
//   is OPFS-only and always misses in tauri, which used to make every
//   restored recording (this peer's own, after a reload, or any other
//   peer's) fall through resolveAudioBytes() straight to null instead of
//   ever reading the bytes back.
//   waveformSamples are stored in the Automerge doc so collaborators can see
//   the waveform without playing the audio — but the audio bytes themselves
//   never travel with the doc. on a peer that didn't make the recording,
//   getBlobData() misses locally; resolveAudioBytes() (see below) falls
//   back to snatching the blob from a canvas peer, same as file.ts's
//   checkBlobLocality()/snatchBlob() flow, before handing back a playable
//   object URL.
//
// device selection:
//   selected deviceId persisted.
//   device labels are only available after mic permission is granted; we
//   re-enumerate after the first successful getUserMedia call.
//   device picker is in the property tray (widgetActions), not the header bar.

import { Container, Graphics, Rectangle, Text } from "pixi.js";
import { z } from "zod";
import { isTauriMode, dispatch } from "../src/p2p/tauri-transport";
import { getBlobData as getBrowserBlobData, storeBlobFromFile } from "../src/storage/blob-store";
import { base64Encode, base64Decode } from "@freqhole/reliquary/worker";
import { checkBlobLocality, freeUpLocalBlobCopy } from "../src/file-utils/blob-locality";
import { snatchBlob, BlobAccessDeniedError } from "../src/file-utils/snatch";
import { getMediaPlaybackUrl } from "../src/media";
import { interleaveChannels, encodeWav } from "./animaniac/export/wav-encode";
import {
  getLocalNodeId,
  type BlobLocalityInfo,
  type PeersMap,
  type SnatchOptions,
} from "../src/file-utils/file-shared";
import { sendFriendRequest } from "../src/p2p/friendz-bridge";
import { registerPendingBlobRetry } from "../src/p2p/pending-blob-access";
import {
  isTransparent,
  type CompactInfo,
  type HeaderAction,
  type WidgetAction,
  type WidgetController,
  type WidgetFactory,
  type WidgetMountContext,
} from "../src/widgets/widget-types";

// ---------------------------------------------------------------------------
// schema
// ---------------------------------------------------------------------------

export const audioRecordingSchema = z.object({
  /** sha256 blob ID of the recorded audio; empty = no recording */
  blobId: z.string().default(""),
  /** original filename */
  filename: z.string().default(""),
  /** MIME type of the recorded audio */
  mime: z.string().default("audio/webm"),
  /** file size in bytes */
  size: z.number().default(0),
  /** blake3 content hash (needed for verified P2P snatch on a remote peer) */
  blake3: z.string().default(""),
  /** node IDs that have snatched (or recorded) this blob — used to target
   *  peer downloads when a remote peer needs to play it back. */
  snatchedBy: z.array(z.string()).default([]),
  /** recording duration in seconds */
  duration: z.number().default(0),
  /** widget background color; -1 = transparent */
  bgColor: z.number().default(0x1e1e2e),
  /** border color; -1 = transparent (no border) */
  borderColor: z.number().default(-1),
  /** border width in pixels; 0 = no border */
  borderWidth: z.number().default(0),
  /**
   * downsampled amplitude envelope (0–1 per sample) stored after recording.
   * lets collaborators see the waveform shape without needing the audio file.
   */
  waveformSamples: z.array(z.number()).default([]),
  /**
   * preferred audio input device label (human-readable).
   * stored in the doc so the widget remembers the chosen device.
   * empty string = system default. matched against enumerateDevices() labels;
   * if the label isn\'t found on this machine, recording falls back to default.
   */
  deviceLabel: z.string().default(""),
  /** gain multiplier applied to the recording (1 = original, unamplified) —
   *  purely a UI/preview value; playback amplification only takes effect
   *  once `gainRenditionBlobId` is set via "apply volume". */
  gainValue: z.number().default(1),
  /** a re-rendered copy of the original recording with `gainValue` baked
   *  into the samples — empty when no gain has been applied, in which
   *  case playback uses `blobId` directly. */
  gainRenditionBlobId: z.string().default(""),
  gainRenditionBlake3: z.string().default(""),
  gainRenditionMime: z.string().default(""),
  gainRenditionSize: z.number().default(0),
  /** node IDs that have snatched (or rendered) the gain rendition blob —
   *  kept separate from `snatchedBy` since it's a DIFFERENT blob; peers
   *  who only ever fetched the original would otherwise be wrongly
   *  reported as having the rendition too. */
  gainRenditionSnatchedBy: z.array(z.string()).default([]),
});

export type AudioRecordingState = z.infer<typeof audioRecordingSchema>;

// ---------------------------------------------------------------------------
// colors
// ---------------------------------------------------------------------------

const COLOR_RECORD = 0xef4444; // red — record button / live waveform
const COLOR_RECORD_DIM = 0x7f1d1d; // dimmed red — requesting state
const COLOR_PRIMARY = 0xd946ef; // magenta — ready state, play button, waveform
const COLOR_PLAY = 0xa21caf; // deep magenta — played-progress bars during playback
const COLOR_MUTED = 0x334155; // slate — processing / disabled
const COLOR_TEXT = 0xf8fafc; // near-white — status text
const COLOR_MUTED_TEXT = 0x94a3b8; // slate-400 — info / secondary text
const COLOR_ERROR = 0xf87171; // red-400 — error message

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function fmtDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function pickMimeType(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4",
  ];
  for (const m of candidates) {
    try {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      // ignore
    }
  }
  return "";
}

function mimeToExt(mime: string): string {
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("mp4") || mime.includes("m4a")) return "m4a";
  return "webm";
}

/**
 * sweep angle (in radians) for a determinate fetch-progress ring, given a
 * 0..1 fraction. clamps out-of-range input (defensive — snatchBlob's
 * onProgress should already report values in range) so a stray fraction
 * never draws a nonsensical arc. exported so the arc math can be unit
 * tested without a real canvas/PixiJS renderer.
 */
export function fetchRingAngleForFraction(fraction: number): number {
  const clamped = Math.max(0, Math.min(1, fraction));
  return clamped * Math.PI * 2;
}

// ---------------------------------------------------------------------------
// blob resolution — shared by getPlaybackUrl() below and unit-tested directly.
// ---------------------------------------------------------------------------

/** minimal doc fields needed to resolve playable audio bytes */
export interface AudioBlobRef {
  blobId: string;
  filename: string;
  mime: string;
  size: number;
  blake3: string;
}

/** injectable dependencies — lets the resolution algorithm be unit-tested
 *  without OPFS/IndexedDB/P2P plumbing. the widget wires in the real
 *  implementations from the blob store / file-utils. */
export interface ResolveAudioBytesDeps {
  getBlobData: (blobId: string) => Promise<ArrayBuffer | null>;
  checkBlobLocality: (blobId: string, blake3?: string) => Promise<BlobLocalityInfo>;
  snatchBlob: (
    info: AudioBlobRef & { domain: string },
    peers: PeersMap,
    options?: SnatchOptions
  ) => Promise<{ blobId: string; blake3?: string | null }>;
  getLocalNodeId: () => Promise<string | null>;
}

export interface ResolvedAudioBytes {
  buffer: ArrayBuffer;
  /** the blob ID actually used to fetch the bytes (may differ from the doc's
   *  blobId after a sha256-dedup rekey during snatch) */
  blobId: string;
  blake3: string;
  /** set when a P2P snatch happened — the caller should record this node in
   *  the doc's `snatchedBy` list so other peers can target it for downloads */
  snatchedByNodeId: string | null;
}

/**
 * read a recording's bytes back by blob id (== blake3 hex), regardless of
 * mode. browser mode reads OPFS directly; tauri mode has no OPFS mirror
 * (see this file's module doc comment) so it goes through the rust
 * `blob_get` dispatch instead, decoding the returned base64 payload.
 *
 * this is the `getBlobData` implementation `resolveAudioBytes()` needs in
 * tauri mode \u2014 the plain OPFS-only `getBlobData` re-exported from
 * blob-store.ts always misses there, which used to make every restored
 * recording (local or a peer's) resolve to nothing.
 */
export async function getAudioBlobData(blobId: string): Promise<ArrayBuffer | null> {
  if (!isTauriMode()) return getBrowserBlobData(blobId);

  try {
    const response = (await dispatch("blob_get", { blake3: blobId })) as {
      data?: string;
    } | null;
    if (!response?.data) return null;
    const bytes = await base64Decode(response.data);
    // slice() (no args) copies into a freshly allocated ArrayBuffer of
    // exactly the right size — .buffer alone can be larger than the view
    // (or, per its type, a SharedArrayBuffer) and isn't safe to hand back.
    return bytes.slice().buffer;
  } catch {
    return null;
  }
}

/**
 * add a node id to a `snatchedBy` list, deduped. used both right after a
 * fresh recording is stored (the recorder always has the blob locally, so
 * it must be recorded as a snatcher immediately — otherwise a hub or peer
 * has no one to target for a snatch until some OTHER peer happens to fetch
 * it first) and after a P2P snatch (the snatching peer now has it too).
 */
export function addSnatcher(snatchedBy: string[] | undefined, nodeId: string | null): string[] {
  const list = snatchedBy ? [...snatchedBy] : [];
  if (nodeId && !list.includes(nodeId)) list.push(nodeId);
  return list;
}

/**
 * resolve playable audio bytes for a recording, fetching from a canvas peer
 * when the bytes aren't local (e.g. viewing another peer's recording — the
 * automerge doc syncs fine, but the audio bytes never crossed the wire on
 * their own). mirrors file.ts's "check locality, then snatch" flow.
 *
 * returns `null` when there's no blob, the bytes are genuinely local but
 * unreadable, or no peer has them.
 */
export async function resolveAudioBytes(
  ref: AudioBlobRef,
  peers: PeersMap | undefined,
  deps: ResolveAudioBytesDeps,
  onProgress?: (fraction: number) => void,
  isPeerOnline?: (nodeId: string) => boolean
): Promise<ResolvedAudioBytes | null> {
  if (!ref.blobId) return null;

  // fast path — bytes already local (original recorder, or a peer that
  // already snatched them earlier this session).
  const local = await deps.getBlobData(ref.blobId);
  if (local) {
    return { buffer: local, blobId: ref.blobId, blake3: ref.blake3, snatchedByNodeId: null };
  }

  // confirm the blob is genuinely remote before paying for a P2P round-trip —
  // a "local" locality result with a getBlobData() miss means something else
  // is wrong (e.g. OPFS read failure), not a missing blob worth snatching.
  const info = await deps.checkBlobLocality(ref.blobId, ref.blake3);
  if (info.locality === "local") return null;

  if (!peers || Object.keys(peers).length === 0) return null;

  const result = await deps.snatchBlob(
    { ...ref, domain: "audio" },
    peers,
    { onProgress, isPeerOnline }
  );

  const buffer = await deps.getBlobData(result.blobId);
  if (!buffer) return null;

  const localNodeId = await deps.getLocalNodeId();

  return {
    buffer,
    blobId: result.blobId,
    blake3: result.blake3 ?? ref.blake3,
    snatchedByNodeId: localNodeId,
  };
}

// ---------------------------------------------------------------------------
// widget
// ---------------------------------------------------------------------------

type RecordState =
  | "idle" // no recording; large record button
  | "requesting" // waiting for getUserMedia permission
  | "recording" // recording in progress
  | "processing" // finalizing + storing blob
  | "ready" // has a recording; play button shown
  | "fetching" // remote peer: downloading blob bytes before playback
  | "playing" // playing back the recording
  | "error" // getUserMedia, storage, or remote-fetch failure
  | "needs-friend" // only non-friend peers have the blob; tap to send a friend request
  | "friend-requested"; // friend request sent; waiting to retry the fetch

export const audioRecordingWidget: WidgetFactory<typeof audioRecordingSchema> = {
  type: "audio-recording",
  metadata: {
    name: "audio recording",
    description: "Record audio from your microphone",
    version: "0.1.0",
    category: "media",
    defaultWidth: 320,
    defaultHeight: 160,
  },
  schema: audioRecordingSchema,
  editableProps: [
    { key: "bgColor", label: "background", type: "color" as const, default: 0x1e1e2e },
    { key: "borderColor", label: "border", type: "color" as const, default: -1 },
    { key: "borderWidth", label: "border width", type: "number" as const, min: 0, default: 0 },
  ],

  getCompactInfo: (state: AudioRecordingState): CompactInfo => ({
    label: state.filename ? state.filename.replace(/\.[^.]+$/, "") : "audio recording",
    domain: "audio",
    blobId: state.blobId || undefined,
    mime: state.mime || undefined,
    filename: state.filename || undefined,
    blake3: state.blake3 || undefined,
    size: state.size || undefined,
    snatchedBy: state.snatchedBy?.length ? state.snatchedBy.map(String) : undefined,
  }),

  create(ctx: WidgetMountContext<typeof audioRecordingSchema>): WidgetController {
    let cw = ctx.width;
    let ch = ctx.height;

    // set when the widget is destroyed; async handlers (chiefly
    // getPlaybackUrl's P2P snatch resolution, which has no real
    // cancellation — see destroy() below) check this to bail out instead
    // of writing to a torn-down doc/pixi tree.
    let destroyed = false;

    // ── transient recording state ────────────────────────────────────────────
    let recState: RecordState = ctx.doc.current.blobId ? "ready" : "idle";
    let mediaRecorder: MediaRecorder | null = null;
    let mediaStream: MediaStream | null = null;
    let audioChunks: Blob[] = [];
    let audioCtx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let recStartTime = 0;
    let elapsedSecs = 0;
    /** amplitude samples (0–1) collected during recording */
    let liveSamples: number[] = [];
    /**
     * amplitude snapshot for display — loaded from doc on mount (if present)
     * and written back to doc after recording.
     */
    let capturedSamples: number[] = [...(ctx.doc.current.waveformSamples ?? [])];
    let waveRafId: number | null = null;

    // only the peer who created this widget can use the initial "tap to
    // record" step — widgets with no recorded creator (pre-existing widgets
    // from before this field existed) are unrestricted.
    const iAmCreator = !ctx.canvasStore || ctx.canvasStore.isLocalWidgetCreator(ctx.widgetId);

    // ── transient playback state ─────────────────────────────────────────────
    let audioEl: HTMLAudioElement | null = null;
    /** object URL for the most recently stored/loaded blob */
    let playbackUrl: string | null = null;
    let playbackElapsed = 0;
    let playRafId: number | null = null;
    /** progress label shown in the "fetching" state while snatching a remote blob */
    let fetchProgressText = "downloading…";
    /** progress fraction (0..1) backing the fetch ring; -1 = unknown/indeterminate */
    let fetchProgressFraction = -1;
    /** message shown after a failed remote-fetch attempt (cleared on next try) */
    let fetchErrorMessage = "";
    let fetchRafId: number | null = null;
    /** the peer known to have the blob but not (yet) a friend — see the
     *  "needs-friend"/"friend-requested" states below. */
    let deniedPeerNodeId: string | null = null;
    /** unregisters the pending-retry-on-friend-accept hook (see
     *  requestFriendAndRetry()); called on destroy() so it never fires
     *  against a torn-down widget. */
    let unregisterPendingRetry: (() => void) | null = null;
    /** current rotation offset (radians) for the indeterminate fetch-ring spinner */
    let fetchAnimAngle = 0;

    // ── device selection state ───────────────────────────────────────────────
    // cachedDevices is populated by enumerateDevices() — labels are empty until
    // microphone permission is granted, so we re-enumerate after the first
    // successful getUserMedia call.
    let cachedDevices: MediaDeviceInfo[] = [];

    const enumerateDevices = async (): Promise<void> => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        cachedDevices = all.filter((d) => d.kind === "audioinput");
      } catch {
        // enumerateDevices may be unavailable without a secure context
      }
    };

    // build the options list for the device select prop.
    // called fresh each time the property tray opens the dropdown.
    const DEVICE_DEFAULT = "System default";
    const deviceOptions = (): string[] => [
      DEVICE_DEFAULT,
      ...cachedDevices.map((d) => d.label || `Microphone (${d.deviceId.slice(0, 8)}…)`),
    ];

    // resolve the deviceId to use for getUserMedia from the stored label.
    // treats both empty string (legacy) and "System default" as no constraint.
    const resolveDeviceId = (): string | undefined => {
      const label = ctx.doc.current.deviceLabel;
      if (!label || label === DEVICE_DEFAULT) return undefined;
      return cachedDevices.find((d) => d.label === label)?.deviceId;
    };

    void enumerateDevices();

    // ── pixi containers ──────────────────────────────────────────────────────
    const container = new Container();
    container.eventMode = "static";

    const bgGfx = new Graphics();
    bgGfx.eventMode = "static";
    bgGfx.hitArea = new Rectangle(0, 0, cw, ch);
    container.addChild(bgGfx);

    const waveGfx = new Graphics();
    waveGfx.eventMode = "none";
    container.addChild(waveGfx);

    const micGfx = new Graphics();
    micGfx.eventMode = "none";
    container.addChild(micGfx);

    const btnGfx = new Graphics();
    btnGfx.eventMode = "static";
    btnGfx.cursor = "pointer";
    container.addChild(btnGfx);

    const statusText = new Text({
      text: "",
      style: {
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 13,
        fill: COLOR_TEXT,
        align: "center",
      },
      resolution: 2,
    });
    statusText.eventMode = "none";
    statusText.anchor.set(0.5, 0);
    container.addChild(statusText);

    const infoText = new Text({
      text: "",
      style: {
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 11,
        fill: COLOR_MUTED_TEXT,
        align: "center",
      },
      resolution: 2,
    });
    infoText.eventMode = "none";
    infoText.anchor.set(0.5, 0);
    container.addChild(infoText);

    const errorText = new Text({
      text: "",
      style: {
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 12,
        fill: COLOR_ERROR,
        align: "center",
        wordWrap: true,
        wordWrapWidth: cw - 32,
      },
      resolution: 2,
    });
    errorText.eventMode = "none";
    errorText.anchor.set(0.5, 0.5);
    container.addChild(errorText);

    // ── layout constants ─────────────────────────────────────────────────────
    const BTN_R = 26;
    const BTN_BOT = 14;
    const STAT_TOP = 12;
    const INFO_TOP = 28;

    // ── fetch (remote-download) progress ring constants ────────────────────
    const FETCH_RING_R = BTN_R + 4;
    const FETCH_RING_WIDTH = 3;
    const FETCH_RING_SWEEP = Math.PI / 2; // ~90° indeterminate arc while fraction is unknown
    const FETCH_RING_SPEED = (Math.PI * 2) / 1200; // radians/ms — full spin every 1.2s

    // ── drawing helpers ──────────────────────────────────────────────────────
    const drawBg = () => {
      const { bgColor, borderColor, borderWidth } = ctx.doc.current;
      bgGfx.clear();

      if (!isTransparent(bgColor)) {
        bgGfx.rect(0, 0, cw, ch);
        bgGfx.fill({ color: bgColor });
      }

      const bw = borderWidth ?? 0;
      if (bw > 0 && !isTransparent(borderColor ?? -1)) {
        bgGfx.rect(0, 0, cw, ch);
        bgGfx.stroke({ color: borderColor, width: bw });
      }

      bgGfx.hitArea = new Rectangle(0, 0, cw, ch);
    };

    const drawMic = (visible: boolean) => {
      micGfx.clear();
      if (!visible) return;
      const cx = cw / 2;
      const cy = ch / 2 - 10;
      const mw = 14;
      const mh = 22;
      const r = mw / 2;
      micGfx.roundRect(cx - mw / 2, cy - mh / 2, mw, mh, r);
      micGfx.fill({ color: COLOR_PRIMARY, alpha: 0.35 });
      micGfx.rect(cx - 1, cy + mh / 2, 2, 8);
      micGfx.fill({ color: COLOR_MUTED_TEXT, alpha: 0.4 });
      micGfx.rect(cx - 8, cy + mh / 2 + 7, 16, 2);
      micGfx.fill({ color: COLOR_MUTED_TEXT, alpha: 0.4 });
    };

    const btnCY = () => ch - BTN_R - BTN_BOT;

    const drawBtn = () => {
      btnGfx.clear();
      const bx = cw / 2;
      const by = btnCY();

      switch (recState) {
        case "idle":
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_RECORD });
          btnGfx.circle(bx, by, BTN_R * 0.42);
          btnGfx.fill({ color: 0xffffff, alpha: 0.88 });
          break;

        case "requesting":
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_RECORD_DIM });
          break;

        case "recording": {
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_RECORD });
          const sq = BTN_R * 0.4;
          btnGfx.rect(bx - sq, by - sq, sq * 2, sq * 2);
          btnGfx.fill({ color: 0xffffff, alpha: 0.88 });
          break;
        }

        case "processing":
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_MUTED });
          break;

        case "fetching": {
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_MUTED });
          btnGfx.circle(bx, by, BTN_R * 0.38);
          btnGfx.stroke({ color: COLOR_PRIMARY, width: 2, alpha: 0.6 });

          // background track for the progress ring
          btnGfx.circle(bx, by, FETCH_RING_R);
          btnGfx.stroke({ color: COLOR_MUTED, width: FETCH_RING_WIDTH, alpha: 0.5 });

          // active arc: determinate sweep from 12 o'clock when the fraction is
          // known, otherwise a rotating indeterminate arc.
          const top = -Math.PI / 2;
          let arcStart: number;
          let arcEnd: number;
          if (fetchProgressFraction >= 0) {
            arcStart = top;
            arcEnd = top + fetchRingAngleForFraction(fetchProgressFraction);
          } else {
            arcStart = top + fetchAnimAngle;
            arcEnd = arcStart + FETCH_RING_SWEEP;
          }
          btnGfx.arc(bx, by, FETCH_RING_R, arcStart, arcEnd);
          btnGfx.stroke({ color: COLOR_PRIMARY, width: FETCH_RING_WIDTH });
          break;
        }

        case "ready": {
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_PRIMARY });
          const ox = 3;
          btnGfx.moveTo(bx - BTN_R * 0.3 + ox, by - BTN_R * 0.44);
          btnGfx.lineTo(bx + BTN_R * 0.5 + ox, by);
          btnGfx.lineTo(bx - BTN_R * 0.3 + ox, by + BTN_R * 0.44);
          btnGfx.closePath();
          btnGfx.fill({ color: 0xffffff, alpha: 0.9 });
          break;
        }

        case "playing": {
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_PLAY });
          const bw = 5;
          const bh = BTN_R * 0.72;
          btnGfx.rect(bx - bw - 3, by - bh / 2, bw, bh);
          btnGfx.fill({ color: 0xffffff, alpha: 0.9 });
          btnGfx.rect(bx + 3, by - bh / 2, bw, bh);
          btnGfx.fill({ color: 0xffffff, alpha: 0.9 });
          break;
        }

        case "error":
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_MUTED });
          btnGfx.circle(bx, by, BTN_R * 0.38);
          btnGfx.stroke({ color: 0xffffff, width: 2, alpha: 0.6 });
          break;

        case "needs-friend":
        case "friend-requested":
          btnGfx.circle(bx, by, BTN_R);
          btnGfx.fill({ color: COLOR_MUTED });
          btnGfx.circle(bx, by, BTN_R * 0.38);
          btnGfx.stroke({
            color: 0xffffff,
            width: 2,
            alpha: recState === "friend-requested" ? 0.3 : 0.6,
          });
          break;
      }

      btnGfx.hitArea = new Rectangle(bx - BTN_R, by - BTN_R, BTN_R * 2, BTN_R * 2);
    };

    const drawWave = (samples: number[], progress?: number) => {
      waveGfx.clear();

      const waveTop = STAT_TOP + 26;
      const waveBot = btnCY() - BTN_R - 8;
      const waveH = waveBot - waveTop;
      if (waveH < 6) return;

      const cx = waveTop + waveH / 2;
      const barCount = 50;
      const totalW = cw - 32;
      const gap = totalW / barCount;
      const barW = Math.max(1.5, gap - 1.5);

      if (samples.length === 0) {
        const flatColor = recState === "recording" ? COLOR_RECORD : COLOR_PRIMARY;
        waveGfx.rect(16, cx - 1, totalW, 2);
        waveGfx.fill({ color: flatColor, alpha: 0.25 });
        return;
      }

      for (let i = 0; i < barCount; i++) {
        const srcIdx = Math.floor((i / barCount) * samples.length);
        const amp = Math.max(0.04, samples[srcIdx] ?? 0.04);
        const barH = Math.max(2, amp * waveH);
        const x = 16 + i * gap;
        const y = cx - barH / 2;

        let color: number;
        let alpha: number;

        if (progress !== undefined) {
          const played = i / barCount < progress;
          color = played ? COLOR_PLAY : COLOR_PRIMARY;
          alpha = played ? 0.9 : 0.35;
        } else if (recState === "recording") {
          color = COLOR_RECORD;
          alpha = 0.6 + amp * 0.4;
        } else {
          color = COLOR_PRIMARY;
          alpha = 0.5 + amp * 0.45;
        }

        waveGfx.roundRect(x, y, barW, barH, 1.5);
        waveGfx.fill({ color, alpha });
      }
    };

    const updateTexts = () => {
      const state = ctx.doc.current;
      drawMic(recState === "idle");

      switch (recState) {
        case "idle":
          statusText.text = iAmCreator ? "tap to record" : "waiting for recording";
          infoText.text = "";
          errorText.text = "";
          break;
        case "requesting":
          statusText.text = "requesting mic…";
          infoText.text = "";
          errorText.text = "";
          break;
        case "recording":
          statusText.text = `● REC  ${fmtDuration(elapsedSecs)}`;
          infoText.text = "";
          errorText.text = "";
          break;
        case "processing":
          statusText.text = "saving…";
          infoText.text = "";
          errorText.text = "";
          break;
        case "fetching":
          statusText.text = fetchProgressText;
          infoText.text = "";
          errorText.text = "";
          break;
        case "ready":
          statusText.text = fmtDuration(state.duration);
          infoText.text = fetchErrorMessage || fmtBytes(state.size);
          errorText.text = "";
          break;
        case "playing":
          statusText.text = `${fmtDuration(playbackElapsed)} / ${fmtDuration(state.duration)}`;
          infoText.text = "";
          errorText.text = "";
          break;
        case "error":
          statusText.text = "";
          infoText.text = "";
          errorText.text = iAmCreator
            ? "mic access denied\ntap to try again"
            : "mic access denied";
          break;
        case "needs-friend":
          statusText.text = "";
          infoText.text = "";
          errorText.text = "only a non-friend peer has this recording\ntap to send a friend request";
          break;
        case "friend-requested":
          statusText.text = "";
          infoText.text = "";
          errorText.text = "friend request sent\nwill retry automatically once accepted";
          break;
      }
    };

    const layout = () => {
      statusText.x = cw / 2;
      statusText.y = STAT_TOP;

      infoText.x = cw / 2;
      infoText.y = INFO_TOP;

      errorText.x = cw / 2;
      errorText.y = ch / 2 - BTN_R - 10;
      (errorText.style as any).wordWrapWidth = cw - 32;
    };

    const refresh = () => {
      drawBg();
      layout();
      updateTexts();
      drawBtn();

      const samples =
        recState === "recording"
          ? liveSamples
          : recState === "ready" || recState === "playing"
            ? capturedSamples
            : [];

      const progress =
        recState === "playing"
          ? playbackElapsed / Math.max(0.001, ctx.doc.current.duration)
          : undefined;

      drawWave(samples, progress);
    };

    refresh();

    // ── waveform animation during recording ──────────────────────────────────
    const startWaveAnim = () => {
      if (!analyser) return;
      const bufLen = analyser.frequencyBinCount;
      const dataArr = new Uint8Array(bufLen);
      let frameCount = 0;

      const tick = () => {
        if (recState !== "recording") return;
        waveRafId = requestAnimationFrame(tick);
        analyser!.getByteTimeDomainData(dataArr);

        let sum = 0;
        for (let i = 0; i < bufLen; i++) {
          const v = (dataArr[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / bufLen);

        frameCount++;
        if (frameCount % 4 === 0) liveSamples.push(rms);

        elapsedSecs = (Date.now() - recStartTime) / 1000;
        updateTexts();
        drawWave(liveSamples);
      };
      waveRafId = requestAnimationFrame(tick);
    };

    const stopWaveAnim = () => {
      if (waveRafId !== null) {
        cancelAnimationFrame(waveRafId);
        waveRafId = null;
      }
    };

    // ── fetch (remote-download) progress ring animation ──────────────────────
    const startFetchAnim = () => {
      let lastTime = performance.now();
      const tick = (now: number) => {
        if (recState !== "fetching") return;
        fetchRafId = requestAnimationFrame(tick);
        const dt = now - lastTime;
        lastTime = now;
        fetchAnimAngle = (fetchAnimAngle + dt * FETCH_RING_SPEED) % (Math.PI * 2);
        drawBtn();
      };
      fetchRafId = requestAnimationFrame(tick);
    };

    const stopFetchAnim = () => {
      if (fetchRafId !== null) {
        cancelAnimationFrame(fetchRafId);
        fetchRafId = null;
      }
    };

    // ── playback animation ───────────────────────────────────────────────────
    const startPlayAnim = () => {
      const dur = Math.max(0.001, ctx.doc.current.duration);
      const tick = () => {
        if (recState !== "playing" || !audioEl) return;
        playRafId = requestAnimationFrame(tick);
        playbackElapsed = audioEl.currentTime;
        updateTexts();
        drawWave(capturedSamples, playbackElapsed / dur);
        ctx.setTitleProgress?.(playbackElapsed / dur);
      };
      playRafId = requestAnimationFrame(tick);
    };

    const stopPlayAnim = () => {
      if (playRafId !== null) {
        cancelAnimationFrame(playRafId);
        playRafId = null;
      }
    };

    // ── recording logic ──────────────────────────────────────────────────────
    const startRecording = async () => {
      if (recState === "requesting" || recState === "recording") return;
      recState = "requesting";
      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());

      let stream: MediaStream;
      const deviceId = resolveDeviceId();
      const audioConstraints: boolean | MediaTrackConstraints = deviceId
        ? { deviceId: { exact: deviceId } }
        : true;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false,
        });
      } catch {
        recState = "error";
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        return;
      }

      mediaStream = stream;
      audioCtx = new AudioContext();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);

      const mime = pickMimeType();
      audioChunks = [];
      liveSamples = [];
      mediaRecorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.push(e.data);
      };
      mediaRecorder.onstart = () => {
        recState = "recording";
        recStartTime = Date.now();
        elapsedSecs = 0;
        startWaveAnim();
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        // re-enumerate now that permission is granted — labels will be populated
        void enumerateDevices();
      };
      mediaRecorder.onstop = () => void finishRecording();
      mediaRecorder.start(100);
    };

    const stopRecording = () => {
      if (recState !== "recording") return;
      stopWaveAnim();
      elapsedSecs = (Date.now() - recStartTime) / 1000;
      recState = "processing";
      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());
      mediaRecorder?.stop();
      mediaStream?.getTracks().forEach((t) => t.stop());
      mediaStream = null;
    };

    const finishRecording = async () => {
      const durationSecs = elapsedSecs;
      const recMime = mediaRecorder?.mimeType ?? "audio/webm";
      const ext = mimeToExt(recMime);
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      const filename = `recording-${ts}.${ext}`;

      const recordedBlob = new Blob(audioChunks, { type: recMime });

      audioChunks = [];
      capturedSamples = [...liveSamples];
      liveSamples = [];

      await audioCtx?.close();
      audioCtx = null;
      analyser = null;

      if (playbackUrl) URL.revokeObjectURL(playbackUrl);
      playbackUrl = URL.createObjectURL(recordedBlob);

      try {
        // tauri mode: route through rust so a real blake3 gets computed and
        // the bytes are pre-warmed into iroh-blobs' FsStore (servable to
        // peers) — see the module doc comment above for why the browser-
        // mode storeBlobFromFile() path can't be used here, and why there's
        // no OPFS/IndexedDB mirror.
        let record: { blob_id: string; sha256: string; blake3: string; size: number; mime: string };
        if (isTauriMode()) {
          const buffer = await recordedBlob.arrayBuffer();
          const base64Data = await base64Encode(buffer);
          const response = (await dispatch("blob_insert", {
            filename,
            mime: recMime,
            data: base64Data,
          })) as {
            blake3: string;
            iroh_hash: string;
            filename: string | null;
            mime: string | null;
            size: number;
          };

          const resolvedMime = response.mime || recMime;

          record = {
            blob_id: response.blake3,
            sha256: "",
            blake3: response.blake3,
            size: response.size,
            mime: resolvedMime,
          };
        } else {
          const file = new File([recordedBlob], filename, { type: recMime });
          const fileRecord = await storeBlobFromFile(file, { metadata: { domain: "audio" } });
          record = {
            blob_id: fileRecord.blob_id,
            sha256: fileRecord.sha256 ?? "",
            blake3: fileRecord.blake3 || fileRecord.blob_id,
            size: fileRecord.size,
            mime: fileRecord.mime,
          };
        }
        // the recorder has the blob locally the instant it's stored — record
        // that now, not just after some other peer later snatches it. without
        // this, a hub or peer trying to snatch a freshly-recorded blob has no
        // `snatchedBy` entry to target until someone else fetches it first.
        const localNodeId = await getLocalNodeId();

        ctx.doc.change((d) => {
          d.blobId = record.blob_id;
          d.filename = filename;
          d.mime = recMime;
          d.size = record.size;
          d.blake3 = record.blake3;
          d.duration = durationSecs;
          d.snatchedBy = addSnatcher(d.snatchedBy, localNodeId);
          // persist waveform — downsample to ≤200 points to keep doc size small
          const MAX_SAMPLES = 200;
          if (capturedSamples.length > MAX_SAMPLES) {
            const step = capturedSamples.length / MAX_SAMPLES;
            d.waveformSamples = Array.from(
              { length: MAX_SAMPLES },
              (_, i) => capturedSamples[Math.floor(i * step)]
            );
          } else {
            d.waveformSamples = [...capturedSamples];
          }
        });

        recState = "ready";
      } catch (err) {
        console.error("[audio-recording] failed to store blob:", err);
        recState = "error";
      }

      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());
    };

    // ── playback logic ───────────────────────────────────────────────────────
    // resolves an object URL for the recorded audio, fetching the bytes from
    // a canvas peer when they aren't local yet (see resolveAudioBytes above
    // for the "check locality, then snatch" algorithm).

    /** forces the next getPlaybackUrl() call to re-resolve — call whenever
     *  which blob playback SHOULD use changes underneath it (a gain
     *  rendition applied/cleared). */
    function invalidatePlaybackCache(): void {
      if (playbackUrl) {
        URL.revokeObjectURL(playbackUrl);
        playbackUrl = null;
      }
    }

    const getPlaybackUrl = async (): Promise<string | null> => {
      if (playbackUrl) return playbackUrl;

      // an applied gain rendition (see "apply volume" below) always wins
      // over the original recording — the whole point of committing one.
      const state = ctx.doc.current;
      const usingRendition = !!state.gainRenditionBlobId;
      const blobId = usingRendition ? state.gainRenditionBlobId : state.blobId;
      const mime = usingRendition ? state.gainRenditionMime : state.mime;
      const blake3 = usingRendition ? state.gainRenditionBlake3 : state.blake3;
      const size = usingRendition ? state.gainRenditionSize : state.size;
      const { filename } = state;
      if (!blobId) return null;

      const peers = ctx.canvasStore?.peers() as PeersMap | undefined;
      let resolved: ResolvedAudioBytes | null = null;
      try {
        resolved = await resolveAudioBytes(
          { blobId, filename, mime, size, blake3 },
          peers,
          { getBlobData: getAudioBlobData, checkBlobLocality, snatchBlob, getLocalNodeId },
          (fraction) => {
            fetchProgressFraction = fraction;
            fetchProgressText =
              fraction >= 0 ? `downloading… ${Math.round(fraction * 100)}%` : "downloading…";
            if (recState === "fetching") updateTexts();
          },
          ctx.canvasStore ? (nodeId: string) => ctx.canvasStore!.isPeerOnline(nodeId) : undefined
        );
      } catch (err) {
        if (err instanceof BlobAccessDeniedError) throw err;
        console.error("[audio-recording] resolveAudioBytes failed:", err);
        return null;
      }

      // the widget may have been deleted while resolveAudioBytes (in
      // particular its P2P snatch path) was in flight. there's no cancellation
      // threaded into the underlying download here (no AbortController is
      // even created for this call), so bail out before writing to ctx.doc
      // or touching anything pixi-side that destroy() already tore down.
      if (destroyed) return null;

      if (!resolved) return null;

      // a P2P snatch happened (not the fast local path) — show the
      // "fetching" state while it ran, then sync the doc with the result.
      if (resolved.snatchedByNodeId !== null) {
        if (resolved.blobId !== blobId || resolved.blake3 !== blake3) {
          ctx.doc.change((d) => {
            if (usingRendition) {
              d.gainRenditionBlobId = resolved!.blobId;
              d.gainRenditionBlake3 = resolved!.blake3;
            } else {
              d.blobId = resolved!.blobId;
              d.blake3 = resolved!.blake3;
            }
          });
        }
        ctx.doc.change((d) => {
          if (usingRendition) {
            d.gainRenditionSnatchedBy = addSnatcher(d.gainRenditionSnatchedBy, resolved!.snatchedByNodeId);
          } else {
            d.snatchedBy = addSnatcher(d.snatchedBy, resolved!.snatchedByNodeId);
          }
        });
      }

      const blob = new Blob([resolved.buffer], { type: mime || "audio/webm" });
      playbackUrl = URL.createObjectURL(blob);
      return playbackUrl;
    };

    // -- gain adjustment: preview/apply/reset ---------------------------
    // see voice-recording.ts's mirror of this same block for the full
    // rationale (offline OfflineAudioContext render, never a live WebAudio
    // tap on a playing <audio> element — see the tauri WebAudio caveat in
    // voice-recording-mouth.ts's own audio code).
    let decodedOriginalBuffer: AudioBuffer | null = null;
    let lastRenderedGain: number | null = null;
    let lastRenderedWavBytes: Uint8Array | null = null;
    let previewEl: HTMLAudioElement | null = null;
    let previewObjectUrl: string | null = null;

    async function ensureDecodedOriginal(): Promise<AudioBuffer | null> {
      if (decodedOriginalBuffer) return decodedOriginalBuffer;
      const { blobId, mime, blake3 } = ctx.doc.current;
      if (!blobId) return null;
      const peers = ctx.canvasStore?.peers() as PeersMap | undefined;
      const url = await getMediaPlaybackUrl(blobId, {
        category: "audio",
        mime: mime || undefined,
        blake3: blake3 || undefined,
        peers,
      });
      if (!url) return null;
      const bytes = await fetch(url).then((r) => r.arrayBuffer());
      const decodeCtx = new OfflineAudioContext(2, 1, 44100);
      decodedOriginalBuffer = await decodeCtx.decodeAudioData(bytes);
      return decodedOriginalBuffer;
    }

    async function renderGainedWav(gain: number): Promise<Uint8Array | null> {
      if (lastRenderedGain === gain && lastRenderedWavBytes) return lastRenderedWavBytes;
      const original = await ensureDecodedOriginal();
      if (!original) return null;
      const renderCtx = new OfflineAudioContext(
        original.numberOfChannels,
        original.length,
        original.sampleRate
      );
      const source = renderCtx.createBufferSource();
      source.buffer = original;
      const gainNode = renderCtx.createGain();
      gainNode.gain.value = gain;
      source.connect(gainNode).connect(renderCtx.destination);
      source.start();
      const rendered = await renderCtx.startRendering();
      const channels: Float32Array[] = [];
      for (let i = 0; i < rendered.numberOfChannels; i++) channels.push(rendered.getChannelData(i));
      const bytes = encodeWav(interleaveChannels(channels), rendered.numberOfChannels, rendered.sampleRate);
      lastRenderedGain = gain;
      lastRenderedWavBytes = bytes;
      return bytes;
    }

    function stopPreviewGain(): void {
      if (previewEl) {
        previewEl.pause();
        previewEl.src = "";
        previewEl = null;
      }
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
        previewObjectUrl = null;
      }
    }

    async function previewGain(): Promise<void> {
      stopPreviewGain();
      const bytes = await renderGainedWav(ctx.doc.current.gainValue);
      if (!bytes || destroyed) return;
      previewObjectUrl = URL.createObjectURL(new Blob([bytes.buffer as ArrayBuffer], { type: "audio/wav" }));
      previewEl = new Audio(previewObjectUrl);
      previewEl.addEventListener("ended", stopPreviewGain, { once: true });
      await previewEl
        .play()
        .catch((err) => console.warn("[audio-recording] gain preview play() rejected:", err));
    }

    function forgetRenditionBlob(blobId: string, blake3: string): void {
      if (!blobId) return;
      freeUpLocalBlobCopy(blobId, blake3 || undefined).catch((err) => {
        console.warn("[audio-recording] failed to free old gain rendition blob (non-fatal):", err);
      });
    }

    async function commitGainRendition(): Promise<void> {
      const gain = ctx.doc.current.gainValue;
      const bytes = await renderGainedWav(gain);
      if (!bytes || destroyed) return;

      const filename = `${ctx.doc.current.filename || "recording"}-gain.wav`;
      let record: { blob_id: string; blake3: string; size: number; mime: string };
      if (isTauriMode()) {
        const base64Data = await base64Encode(bytes.buffer as ArrayBuffer);
        const response = (await dispatch("blob_insert", {
          filename,
          mime: "audio/wav",
          data: base64Data,
        })) as { blake3: string; mime: string | null; size: number };
        record = {
          blob_id: response.blake3,
          blake3: response.blake3,
          size: response.size,
          mime: response.mime || "audio/wav",
        };
      } else {
        const file = new File([bytes.buffer as ArrayBuffer], filename, { type: "audio/wav" });
        const fileRecord = await storeBlobFromFile(file, { metadata: { domain: "audio" } });
        record = {
          blob_id: fileRecord.blob_id,
          blake3: fileRecord.blake3 || fileRecord.blob_id,
          size: fileRecord.size,
          mime: fileRecord.mime,
        };
      }

      const previousRenditionBlobId = ctx.doc.current.gainRenditionBlobId;
      const previousRenditionBlake3 = ctx.doc.current.gainRenditionBlake3;
      // this device has the rendition locally the moment it renders it —
      // record that immediately, same as a fresh recording's own upload.
      const localNodeId = await getLocalNodeId();
      ctx.doc.change((d) => {
        d.gainRenditionBlobId = record.blob_id;
        d.gainRenditionBlake3 = record.blake3;
        d.gainRenditionMime = record.mime;
        d.gainRenditionSize = record.size;
        d.gainRenditionSnatchedBy = addSnatcher([], localNodeId);
      });
      invalidatePlaybackCache();
      if (previousRenditionBlobId && previousRenditionBlobId !== record.blob_id) {
        forgetRenditionBlob(previousRenditionBlobId, previousRenditionBlake3);
      }
    }

    function resetGain(): void {
      stopPreviewGain();
      const previousRenditionBlobId = ctx.doc.current.gainRenditionBlobId;
      const previousRenditionBlake3 = ctx.doc.current.gainRenditionBlake3;
      ctx.doc.change((d) => {
        d.gainValue = 1;
        d.gainRenditionBlobId = "";
        d.gainRenditionBlake3 = "";
        d.gainRenditionMime = "";
        d.gainRenditionSize = 0;
        d.gainRenditionSnatchedBy = [];
      });
      invalidatePlaybackCache();
      if (previousRenditionBlobId) forgetRenditionBlob(previousRenditionBlobId, previousRenditionBlake3);
    }

    const startPlayback = async () => {
      fetchErrorMessage = "";

      // show a "downloading" state while resolving the bytes — usually
      // resolves near-instantly from local storage, but on a remote peer's
      // widget this covers a real P2P snatch instead of a hard error.
      if (!playbackUrl) {
        recState = "fetching";
        fetchProgressText = "downloading…";
        fetchProgressFraction = -1;
        startFetchAnim();
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
      }

      let url: string | null;
      try {
        url = await getPlaybackUrl();
      } catch (err) {
        if (destroyed) return;
        if (err instanceof BlobAccessDeniedError) {
          stopFetchAnim();
          deniedPeerNodeId = err.peerNodeId;
          recState = "needs-friend";
          refresh();
          ctx.setHeaderActions?.(makeHeaderActions());
          return;
        }
        console.error("[audio-recording] getPlaybackUrl failed:", err);
        url = null;
      }
      if (destroyed) return;
      if (!url) {
        console.error("[audio-recording] no playback URL available");
        stopFetchAnim();
        recState = "ready";
        fetchErrorMessage = "playback unavailable";
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        return;
      }

      if (!audioEl) {
        audioEl = document.createElement("audio");
        audioEl.onended = () => {
          recState = "ready";
          playbackElapsed = 0;
          stopPlayAnim();
          refresh();
          ctx.setHeaderActions?.(makeHeaderActions());
          ctx.setTitleProgress?.(0);
        };
      }

      if (audioEl.src !== url) audioEl.src = url;

      try {
        await audioEl.play();
      } catch (err) {
        console.error("[audio-recording] play() failed:", err);
        stopFetchAnim();
        recState = "ready";
        fetchErrorMessage = "playback unavailable";
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        return;
      }

      recState = "playing";
      playbackElapsed = audioEl.currentTime;
      stopFetchAnim();
      startPlayAnim();
      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());
      ctx.setTitleProgress?.(playbackElapsed / Math.max(0.001, ctx.doc.current.duration));
    };

    /** send a friend request to the peer holding this recording, then
     *  automatically retry playback once the request is accepted (see
     *  pending-blob-access.ts). session-only — if the widget is destroyed
     *  before the request is accepted, the retry is simply dropped. */
    const requestFriendAndRetry = async () => {
      const peerNodeId = deniedPeerNodeId;
      if (!peerNodeId) return;

      unregisterPendingRetry?.();
      try {
        await sendFriendRequest(peerNodeId);
      } catch (err) {
        console.error("[audio-recording] sendFriendRequest failed:", err);
        return;
      }
      if (destroyed) return;

      recState = "friend-requested";
      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());

      unregisterPendingRetry = registerPendingBlobRetry(peerNodeId, () => {
        unregisterPendingRetry = null;
        if (destroyed) return;
        void startPlayback();
      });
    };

    const pausePlayback = () => {
      audioEl?.pause();
      recState = "ready";
      stopPlayAnim();
      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());
    };

    const deleteRecording = () => {
      if (recState === "playing") {
        audioEl?.pause();
        stopPlayAnim();
      }
      if (audioEl) {
        audioEl.src = "";
        audioEl = null;
      }
      if (playbackUrl) {
        URL.revokeObjectURL(playbackUrl);
        playbackUrl = null;
      }
      capturedSamples = [];
      playbackElapsed = 0;
      fetchErrorMessage = "";
      recState = "idle";

      ctx.doc.change((d) => {
        d.blobId = "";
        d.filename = "";
        d.size = 0;
        d.blake3 = "";
        d.snatchedBy = [];
        d.duration = 0;
        d.waveformSamples = [];
      });

      refresh();
      ctx.setHeaderActions?.(makeHeaderActions());
      ctx.setTitleProgress?.(null);
    };

    // ── button click handler ─────────────────────────────────────────────────
    btnGfx.on("pointerup", () => {
      switch (recState) {
        case "idle":
        case "error":
          if (ctx.canvasStore?.isLocalViewer()) return;
          if (!iAmCreator) return;
          void startRecording();
          break;
        case "recording":
          if (ctx.canvasStore?.isLocalViewer()) return;
          if (!iAmCreator) return;
          stopRecording();
          break;
        case "ready":
          void startPlayback();
          break;
        case "playing":
          pausePlayback();
          break;
        case "needs-friend":
          void requestFriendAndRetry();
          break;
      }
    });

    // ── header actions — only ■ stop while recording ─────────────────────────
    const makeHeaderActions = (): HeaderAction[] => {
      if (recState === "recording") {
        return [
          {
            id: "stop",
            label: "■ stop",
            active: true,
            onClick: stopRecording,
          },
        ];
      }
      return [];
    };

    // ── doc subscription ─────────────────────────────────────────────────────
    const unsub = ctx.doc.on("change", () => {
      const { blobId, waveformSamples } = ctx.doc.current;

      // sync capturedSamples from doc (collaborative update)
      if (waveformSamples && waveformSamples.length > 0 && capturedSamples.length === 0) {
        capturedSamples = [...waveformSamples];
      }

      if ((recState === "idle" || recState === "error") && blobId) {
        capturedSamples = [...(ctx.doc.current.waveformSamples ?? [])];
        recState = "ready";
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        return;
      }

      if ((recState === "ready" || recState === "playing") && !blobId) {
        if (recState === "playing") {
          audioEl?.pause();
          stopPlayAnim();
        }
        capturedSamples = [];
        playbackElapsed = 0;
        recState = "idle";
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        return;
      }

      if ((recState === "needs-friend" || recState === "friend-requested") && !blobId) {
        unregisterPendingRetry?.();
        unregisterPendingRetry = null;
        deniedPeerNodeId = null;
        capturedSamples = [];
        recState = "idle";
        refresh();
        ctx.setHeaderActions?.(makeHeaderActions());
        return;
      }

      // bgColor / border / other prop change
      drawBg();
    });

    // ── widget actions (property tray) ───────────────────────────────────────
    const widgetActions: WidgetAction[] = [
      { id: "preview-gain", label: "preview volume", onClick: () => void previewGain() },
      { id: "stop-gain-preview", label: "stop preview", onClick: stopPreviewGain },
      { id: "apply-gain", label: "apply volume", onClick: () => void commitGainRendition() },
      { id: "reset-gain", label: "reset volume", onClick: resetGain },
      {
        id: "delete-recording",
        label: "delete recording",
        onClick: deleteRecording,
      },
    ];

    return {
      container,
      headerActions: makeHeaderActions(),
      widgetActions,
      editableProps: [
        { key: "bgColor", label: "background", type: "color" as const, default: 0x1e1e2e },
        { key: "borderColor", label: "border", type: "color" as const, default: -1 },
        { key: "borderWidth", label: "border width", type: "number" as const, min: 0, default: 0 },
        {
          key: "deviceLabel",
          label: "input device",
          type: "select" as const,
          options: deviceOptions,
          default: DEVICE_DEFAULT,
        },
        // last editableProp so it renders immediately above the
        // preview/stop/apply/reset buttons in widgetActions below.
        {
          key: "gainValue",
          label: "volume",
          type: "number" as const,
          default: 1,
          min: 0,
          max: 11,
          step: 0.1,
        },
      ],
      destroy() {
        destroyed = true;
        stopWaveAnim();
        stopPlayAnim();
        stopFetchAnim();
        stopPreviewGain();
        unregisterPendingRetry?.();
        unregisterPendingRetry = null;
        mediaRecorder?.stop();
        mediaStream?.getTracks().forEach((t) => t.stop());
        void audioCtx?.close();
        if (audioEl) {
          audioEl.pause();
          audioEl.src = "";
          audioEl = null;
        }
        if (playbackUrl) {
          URL.revokeObjectURL(playbackUrl);
          playbackUrl = null;
        }
        unsub();
        container.destroy({ children: true });
      },
      resize(w, h) {
        cw = w;
        ch = h;
        bgGfx.hitArea = new Rectangle(0, 0, cw, ch);
        refresh();
      },
    };
  },
};
